import { useFormGuard } from "../../components/shared/UnsavedChangesGuard";
import React, { useState } from "react";
import { Container, Paper, Link, Box, Typography, Stack, Alert } from "@mui/material";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
// import axios from "axios";
import ApiAdmin from "../../config/AppAdmin";
import FormHeader from "./form_header";
import SignInForm from "./signin_form";
import ResetPasswordForm from "./reset_password_form";

// const url = "https://www.bbgrestaurant.com/bbgapi";

const AuthForm = ({ onAuthSubmit }) => {
  const [formData, setFormData] = useState({
    admin_contact: "",
    admin_password: "",
    otp: "",
    new_password: "",
    confirm_password: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const navigate = useNavigate();
  const guard = useFormGuard({ values: formData, saving });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setErrorMessage("");
    setFormData({ ...formData, [name]: value });
  };

  const validateContact = (admin_contact) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10}$/;
    if (!emailRegex.test(admin_contact) && !phoneRegex.test(admin_contact)) {
      toast.error(
        "Invalid contact: Enter a valid email or 10-digit phone number."
      );
      return false;
    }
    return true;
  };
  const handleSignIn = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    if (!validateContact(formData.admin_contact.trim())) return;

    const payload = {
      admin_contact: formData.admin_contact.trim(),
      admin_password: formData.admin_password,
    };

    if (!guard.beginSave()) return;
    setSaving(true);
    try {
      const response = await ApiAdmin.post(`/admin/login`, payload);
      const { role, token, aid } = response.data.data;

      // Step 2: Fetch admin profile data using the token
      const profileResponse = await ApiAdmin.get(`/admin/${aid}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const adminData = profileResponse.data; // Contains the admin's profile data
      // console.log("admin data",adminData);

      localStorage.setItem("userRole", role);
      localStorage.setItem("token", token);
      localStorage.setItem("adminData", JSON.stringify(adminData));

      toast.success("Welcome back.");
      guard.markSaved();
      onAuthSubmit({ isAuthenticated: true, role, adminData }); // Pass the role and authentication status
      navigate("/"); // Redirect immediately to dashboard
    } catch (error) {
      // console.log(error);
      setErrorMessage("Unable to sign in. Check your credentials and try again.");
      // console.error(error);
    } finally { guard.endSave(); setSaving(false); }
  };

  const handleOtpSend = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    if (!validateContact(formData.admin_contact.trim())) return;

    if (!guard.beginSave()) return;
    setSaving(true);
    try {
      const otp = Math.floor(100000 + Math.random() * 900000).toString();
      await ApiAdmin.post(`/admin/save-otp`, {
        admin_contact: formData.admin_contact.trim(),
        otp: otp,
      });
      toast.success("OTP has been sent to your email.");
      setOtpSent(true);
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong.");
    } finally { guard.endSave(); setSaving(false); }
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();

    if (!guard.beginSave()) return;
    setSaving(true);
    try {
      await ApiAdmin.post(`/admin/verify-otp`, {
        admin_contact: formData.admin_contact.trim(),
        otp: formData.otp,
      });
      toast.success("OTP verified successfully.");
      setOtpVerified(true);
    } catch (error) {
      toast.error(error.response?.data?.message || "OTP verification failed.");
    } finally { guard.endSave(); setSaving(false); }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (formData.new_password !== formData.confirm_password) {
      toast.error("Passwords do not match.");
      return;
    }

    if (!guard.beginSave()) return;
    setSaving(true);
    try {
      await ApiAdmin.post(`/admin/reset-password`, {
        admin_contact: formData.admin_contact.trim(),
        new_password: formData.new_password,
      });
      toast.success("Password updated successfully.");
      guard.markSaved({ admin_contact: "", admin_password: "", otp: "", new_password: "", confirm_password: "" });
      setIsResettingPassword(false);
      setOtpSent(false);
      setOtpVerified(false);
      setFormData({
        admin_contact: "",
        admin_password: "",
        otp: "",
        new_password: "",
        confirm_password: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Password reset failed.");
    } finally { guard.endSave(); setSaving(false); }
  };

  const toggleResetPassword = () => {
    setIsResettingPassword(!isResettingPassword);
    setOtpSent(false);
    setOtpVerified(false);
    setFormData({
      admin_contact: "",
      admin_password: "",
      otp: "",
      new_password: "",
      confirm_password: "",
    });
  };

  return (
    <Box component="main" sx={{ minHeight: "100dvh", display: "grid", placeItems: "center", px: 2, py: 5, background: "radial-gradient(ellipse at top, #e8f3ef 0%, #f4f6f8 65%)" }}>
    <Container maxWidth="xs" disableGutters>
      <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 3 }}><Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "primary.main" }} /><Typography variant="subtitle2" color="primary.main">BBG · Restaurant workspace</Typography></Stack>
      <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, boxShadow: "0 16px 48px #182b3610" }}>
        {isResettingPassword ? (
          <>
            <FormHeader title="Reset your password" description="Recover access to your restaurant workspace." />
            <ResetPasswordForm
              saving={saving}
              formData={formData}
              otpSent={otpSent}
              otpVerified={otpVerified}
              handleInputChange={handleInputChange}
              handleOtpSend={handleOtpSend}
              handleOtpVerify={handleOtpVerify}
              handlePasswordReset={handlePasswordReset}
            />
          </>
        ) : (
          <>
            <FormHeader title="Welcome back" description="Sign in to manage your restaurant." />
            {errorMessage && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage}</Alert>}
            <SignInForm
              saving={saving}
              formData={formData}
              showPassword={showPassword}
              handleInputChange={handleInputChange}
              handleSignIn={handleSignIn}
              setShowPassword={setShowPassword}
            />
          </>
        )}
        <Link
          component="button"
          disabled={saving}
          onClick={() => guard.request(() => { toggleResetPassword(); guard.resetBaseline({ admin_contact: "", admin_password: "", otp: "", new_password: "", confirm_password: "" }); })}
          variant="body2"
          sx={{ mt: 2, display: "block", mx: "auto" }}
        >
          {isResettingPassword ? "Back to Sign In" : "Forgot Password?"}
        </Link>
      </Paper>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 3 }}>BBG Admin · Restaurant management</Typography>
    </Container>
    </Box>
  );
};

export default AuthForm;
